import { Loader2, Lock } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface RegisterCredentials {
  username: string
  password: string
  full_name?: string
  email?: string
}

export interface RegisterFormViewProps {
  /** True while the connected register call is in flight. */
  loading: boolean
  /**
   * Server enforced sign-up lock (e.g. 403). When set the form is disabled and
   * a closed-registration notice replaces the inline error.
   */
  registrationClosed: boolean
  /** Inline error message to show (validation or server). Empty string hides it. */
  error: string
  /**
   * Submit the entered, client-validated credentials. Resolve on success (the
   * page navigates away); reject to keep the form usable for a retry.
   */
  onSubmit: (credentials: RegisterCredentials) => Promise<void>
  /** Surface a client-side validation error from the view to the page. */
  onValidationError: (message: string) => void
}

/**
 * Pure presentation + local field state + client-side validation for the
 * register form. Mirrors {@link import('./LoginFormView').LoginFormView} —
 * token-based shadcn Card. The connected `RegisterPage` owns the auth call,
 * the 403→`registrationClosed` mapping, navigation and error message state.
 */
export function RegisterFormView({
  loading,
  registrationClosed,
  error,
  onSubmit,
  onValidationError,
}: RegisterFormViewProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!username || !password) {
      onValidationError(t('auth.register.usernameRequired'))
      return
    }

    if (password !== confirmPassword) {
      onValidationError(t('common.passwordsDoNotMatch'))
      return
    }

    if (password.length < 6) {
      onValidationError(t('auth.register.passwordTooShort'))
      return
    }

    try {
      await onSubmit({
        username,
        password,
        full_name: fullName || undefined,
        email: email || undefined,
      })
    } catch {
      // The connected page maps the error (closed registration / message).
    }
  }

  const disabled = loading || registrationClosed

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t('auth.register.title')}</CardTitle>
        <CardDescription>{t('auth.register.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {registrationClosed && (
          <div className="mb-6 space-y-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" />
              <div className="flex-1 space-y-2">
                <h3 className="text-sm font-semibold text-warning">
                  {t('auth.register.registrationClosed')}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t('auth.register.registrationClosedBody')}
                </p>
                <div className="border-t border-warning/20 pt-2">
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-warning">{t('auth.register.needAccess')}</strong>{' '}
                    {t('auth.register.needAccessBody')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">
              {t('auth.register.username')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="username"
              type="text"
              placeholder={t('auth.register.usernamePlaceholder')}
              value={username}
              onChange={e => setUsername(e.target.value)}
              disabled={disabled}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fullName">{t('auth.register.fullName')}</Label>
            <Input
              id="fullName"
              type="text"
              placeholder={t('auth.register.fullNamePlaceholder')}
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.register.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('auth.register.emailPlaceholder')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              {t('auth.register.password')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="password"
              type="password"
              placeholder={t('auth.register.passwordPlaceholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={disabled}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">
              {t('auth.register.confirmPassword')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={disabled}
              required
            />
          </div>

          {error && (
            <div className="rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="solid"
            disabled={disabled || !username || !password || !confirmPassword}
            className="w-full"
          >
            {registrationClosed ? (
              <>
                <Lock className="mr-2 h-4 w-4" />
                {t('auth.register.registrationClosedBtn')}
              </>
            ) : loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.register.creatingAccount')}
              </>
            ) : (
              t('auth.register.submit')
            )}
          </Button>

          <div className="text-center pt-4">
            <p className="text-sm text-muted-foreground">
              {t('auth.register.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-semibold">
                {t('auth.register.login')}
              </Link>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
